import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  promoSimulatorService,
  PROMO_DEFAULT_MANUFACTURER,
  PROMO_ALL_BRANDS_ID,
  PROMO_ALL_CLASSES_ID,
  PROMO_ALL_CLASSES_OPTION,
  resolvePerformanceBrandParam,
} from "../services/promoSimulatorService";

const catalogCache = {
  manufacturers: new Map(), // key: "projectId"
  brands: new Map(),        // key: "projectId|manufacturer|class"
  classes: new Map(),       // key: "projectId|manufacturer|brand"
};

function mfrKey(manufacturer) {
  return String(manufacturer ?? "").trim() || PROMO_DEFAULT_MANUFACTURER;
}

function projectKey(projectId) {
  return String(projectId ?? "").trim();
}

/**
 * Brands cache key: projectId + manufacturer + optional productClass.
 * When productClass is null/empty the full brand list is cached separately.
 */
function brandsCacheKey(projectId, manufacturer, productClass) {
  return `${projectKey(projectId)}|${mfrKey(manufacturer)}|${String(productClass ?? "").trim()}`;
}

/** Classes cache key: projectId + manufacturer + brand (original dependency). */
function classesCacheKey(projectId, manufacturer, brandForClasses) {
  return `${projectKey(projectId)}|${mfrKey(manufacturer)}|${String(brandForClasses ?? "").trim()}`;
}

async function loadManufacturersCached(projectId) {
  const key = projectKey(projectId);
  const entry = catalogCache.manufacturers.get(key);
  if (entry?.data) return entry.data;
  if (entry?.promise) return entry.promise;

  const promise = promoSimulatorService
    .getManufacturers(projectId)
    .then((data) => {
      const list = Array.isArray(data) ? data : [];
      catalogCache.manufacturers.set(key, { data: list, promise: null });
      return list;
    })
    .catch(() => {
      catalogCache.manufacturers.set(key, { data: [], promise: null });
      return [];
    });

  catalogCache.manufacturers.set(key, { data: null, promise });
  return promise;
}

/**
 * Brands are loaded for a manufacturer, optionally narrowed by productClass.
 * When productClass is null the full brands list for the manufacturer is returned.
 */
async function loadBrandsCached(projectId, manufacturer, productClass) {
  const key = brandsCacheKey(projectId, manufacturer, productClass);
  const entry = catalogCache.brands.get(key);
  if (entry?.data) return entry.data;
  if (entry?.promise) return entry.promise;

  const promise = promoSimulatorService
    .getBrands(mfrKey(manufacturer), productClass || null, projectId)
    .then((data) => {
      catalogCache.brands.set(key, { data: Array.isArray(data) ? data : [], promise: null });
      return catalogCache.brands.get(key).data;
    })
    .catch(() => {
      catalogCache.brands.set(key, { data: [], promise: null });
      return [];
    });

  catalogCache.brands.set(key, { data: null, promise });
  return promise;
}

/** Classes are loaded for a manufacturer + brand (original dependency). */
async function loadClassesCached(projectId, manufacturer, brandForClasses) {
  const key = classesCacheKey(projectId, manufacturer, brandForClasses);
  const entry = catalogCache.classes.get(key);
  if (entry?.data) return entry.data;
  if (entry?.promise) return entry.promise;

  const promise = promoSimulatorService
    .getClasses(brandForClasses, mfrKey(manufacturer), projectId)
    .then((data) => {
      catalogCache.classes.set(key, { data: Array.isArray(data) ? data : [], promise: null });
      return catalogCache.classes.get(key).data;
    })
    .catch(() => {
      catalogCache.classes.set(key, { data: [], promise: null });
      return [];
    });

  catalogCache.classes.set(key, { data: null, promise });
  return promise;
}

/**
 * Loads promo catalog options with module-level caching.
 *
 * Filter order in UI: Manufacturer → Brand → Class  (unchanged from original)
 *
 * Dependencies:
 *  - Classes depend on manufacturer + brand  (original behaviour).
 *  - Brands depend on manufacturer by default; when `productClass` is provided
 *    the list is narrowed to brands that belong to that class, so selecting a
 *    class after selecting a brand reactively filters the brand dropdown.
 *
 * @param {string}      manufacturer
 * @param {string|null} brand          Currently selected brand (drives class list).
 * @param {string|null} productClass   Currently selected class (narrows brand list).
 */
export function usePromoCatalog(manufacturer, brand, productClass) {
  const projectId = useSelector((state) => state.userProject?.projectId ?? null);
  const brandForClasses = useMemo(
    () => resolvePerformanceBrandParam(brand),
    [brand]
  );

  const [apiManufacturers, setApiManufacturers] = useState([]);
  const [manufacturersLoading, setManufacturersLoading] = useState(true);
  const [apiBrands, setApiBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [apiClasses, setApiClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);

  // ── Manufacturers ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (projectId == null) return;
    let cancelled = false;
    const key = projectKey(projectId);
    const cached = catalogCache.manufacturers.get(key)?.data;
    if (cached) {
      setApiManufacturers(cached);
      setManufacturersLoading(false);
      return undefined;
    }
    setManufacturersLoading(true);
    loadManufacturersCached(projectId)
      .then((list) => { if (!cancelled) setApiManufacturers(list); })
      .finally(() => { if (!cancelled) setManufacturersLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  // ── Brands (manufacturer + optional productClass) ─────────────────────────
  useEffect(() => {
    if (projectId == null) return;
    let cancelled = false;
    const key = brandsCacheKey(projectId, manufacturer, productClass);
    const cached = catalogCache.brands.get(key)?.data;
    if (cached) {
      setApiBrands(cached);
      setBrandsLoading(false);
      return undefined;
    }
    setBrandsLoading(true);
    loadBrandsCached(projectId, manufacturer, productClass)
      .then((brands) => { if (!cancelled) setApiBrands(brands); })
      .finally(() => { if (!cancelled) setBrandsLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, manufacturer, productClass]);

  // ── Classes (manufacturer + brand — original dependency) ──────────────────
  useEffect(() => {
    if (projectId == null) return;
    let cancelled = false;
    const key = classesCacheKey(projectId, manufacturer, brandForClasses);
    const cached = catalogCache.classes.get(key)?.data;
    if (cached) {
      setApiClasses(cached);
      setClassesLoading(false);
      return undefined;
    }
    setClassesLoading(true);
    loadClassesCached(projectId, manufacturer, brandForClasses)
      .then((classes) => { if (!cancelled) setApiClasses(classes); })
      .finally(() => { if (!cancelled) setClassesLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, manufacturer, brandForClasses]);

  const manufacturers = useMemo(() => {
    return Array.from(new Set(apiManufacturers)).sort();
  }, [apiManufacturers]);

  const brandOptions = useMemo(
    () => [PROMO_ALL_BRANDS_ID, ...apiBrands.map((b) => b.id)],
    [apiBrands]
  );

  const allClassOptions = useMemo(
    () => [PROMO_ALL_CLASSES_OPTION, ...apiClasses],
    [apiClasses]
  );

  const classOptions = useMemo(
    () => [PROMO_ALL_CLASSES_ID, ...apiClasses.map((c) => c.id)],
    [apiClasses]
  );

  return {
    manufacturers,
    apiBrands,
    apiClasses: allClassOptions,
    brandOptions,
    classOptions,
    manufacturersLoading,
    brandsLoading,
    classesLoading,
  };
}
